<script setup lang="ts">
import AlertMessage from '@/components/banners/AlertMessage.vue';
import OnboardingInvitedUserDetailsForm, {
  UserData,
} from '@/pages/onboarding/components/OnboardingInvitedUserDetailsForm.vue';

import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { CONTACT_SUPPORT_MSG } from '@/data/error-messages';
import analytics, { SEGMENT_EVENTS } from '@/utils/analytics';
import { useProfiling } from '@/composables/useProfiling';
import { RouteNames } from '@/data/routes';
import { EVENT_TYPES } from '@/data/lexisnexis-types';

// ------- Setup & State -------
const store = useStore();
const showErrorMessage = ref(false);
const errorMessage = ref('');
const profiling = useProfiling(RouteNames.ONBOARDING.INVITED.USER_DETAILS, {
  type: EVENT_TYPES.ACCOUNT_CREATION,
});

// ------- Computed Properties -------
const contactDetails = computed(() => store.getters.contactDetails);
const contactSupportMsg = computed(() =>
  CONTACT_SUPPORT_MSG(contactDetails.value?.number),
);
const updateUserRequest = computed(() =>
  store.getters.requestStatus('UPDATE_USER'),
);

// ------- Methods -------
const handleSubmit = async (userData: UserData) => {
  showErrorMessage.value = false;

  await store.dispatchApiAction('UPDATE_USER', userData);

  if (updateUserRequest.value.isError) {
    showErrorMessage.value = true;
    errorMessage.value =
      updateUserRequest.value.errorMessage ||
      'An error occurred while updating your profile.';

    analytics.track(SEGMENT_EVENTS.clear_pay_error, {
      errorMessage: errorMessage.value,
    });
    return false;
  }
  profiling.finish();
  return true;
};
</script>

<template>
  <div class="onboarding-form">
    <AlertMessage v-if="showErrorMessage" type="error">
      {{ contactSupportMsg }}
    </AlertMessage>

    <OnboardingInvitedUserDetailsForm :on-submit="handleSubmit" />
  </div>
</template>

<style scoped>
.onboarding-form {
  max-width: 600px;
  margin: 0 auto;
  padding: 2rem 1rem;
}
</style>
